import React, { FC } from 'react';
import { Navigate, RouteObject } from 'react-router-dom';

import { APP_ROUTES } from '@/shared/config/routes-config';
import { UserRole } from '@/shared/enums/user-role.enum';
import { RouteConfig } from '@/shared/interfaces/routes-config.interface';
import DefaultLayout from '@/shared/layouts/default/default.layout';

const SourcesListContainer = React.lazy(
  () => import('../../containers/sources/sources-list.container')
);
const SourceFormContainer = React.lazy(
  () => import('../../containers/sources/source-form.container')
);
const SourceDetailContainer = React.lazy(
  () => import('../../containers/sources/source-detail.container')
);
const CatalogListContainer = React.lazy(
  () => import('../../containers/catalog/catalog-list.container')
);
const CatalogFormContainer = React.lazy(
  () => import('../../containers/catalog/catalog-form.container')
);
const CatalogDetailContainer = React.lazy(
  () => import('../../containers/catalog/catalog-detail.container')
);
const EvaluateContainer = React.lazy(
  () => import('../../containers/evaluate/evaluate.container')
);
const HistoryListContainer = React.lazy(
  () => import('../../containers/history/history-list.container')
);
const HistoryDetailContainer = React.lazy(
  () => import('../../containers/history/history-detail.container')
);
const CallHistoryCompaniesContainer = React.lazy(
  () => import('../../containers/call-history/call-history-companies.container')
);
const CallHistoryCompanyUsersContainer = React.lazy(
  () => import('../../containers/call-history/call-history-company-users.container')
);
const CallHistoryAccessLogContainer = React.lazy(
  () => import('../../containers/call-history/call-history-access-log.container')
);

const ADMIN_ROLES: UserRole[] = [UserRole.SUPER_ADMIN, UserRole.ADMIN];

const createProtectedRoute = (component: FC, addLayout = false): React.ReactElement => (
  <React.Suspense fallback={<div>Loading...</div>}>
    <DefaultLayout
      {...(addLayout && { addLayout })}
      authenticationRequired={true}
      permittedList={ADMIN_ROLES}
      component={component}
    />
  </React.Suspense>
);

const ROUTES = APP_ROUTES.MARKET_PRICING_MODULE.CHILDREN as RouteConfig;

export const MarketPricingRoutesConfig: RouteObject[] = [
  {
    index: true,
    element: <Navigate to={ROUTES.SOURCES_LIST.FULL_PATH} replace />
  },
  // Sources — static segments before :id (match Leads module FULL_PATH pattern)
  {
    path: ROUTES.SOURCES_ADD.FULL_PATH,
    element: createProtectedRoute(SourceFormContainer, true)
  },
  {
    path: ROUTES.SOURCES_EDIT.FULL_PATH,
    element: createProtectedRoute(SourceFormContainer, true)
  },
  {
    path: ROUTES.SOURCES_DETAIL.FULL_PATH,
    element: createProtectedRoute(SourceDetailContainer, true)
  },
  {
    path: ROUTES.SOURCES_LIST.FULL_PATH,
    element: createProtectedRoute(SourcesListContainer)
  },
  // Catalog
  {
    path: ROUTES.CATALOG_ADD.FULL_PATH,
    element: createProtectedRoute(CatalogFormContainer, true)
  },
  {
    path: ROUTES.CATALOG_EDIT.FULL_PATH,
    element: createProtectedRoute(CatalogFormContainer, true)
  },
  {
    path: ROUTES.CATALOG_DETAIL.FULL_PATH,
    element: createProtectedRoute(CatalogDetailContainer, true)
  },
  {
    path: ROUTES.CATALOG_LIST.FULL_PATH,
    element: createProtectedRoute(CatalogListContainer)
  },
  {
    path: ROUTES.EVALUATE.FULL_PATH,
    element: createProtectedRoute(EvaluateContainer)
  },
  {
    path: ROUTES.HISTORY_DETAIL.FULL_PATH,
    element: createProtectedRoute(HistoryDetailContainer, true)
  },
  {
    path: ROUTES.HISTORY_LIST.FULL_PATH,
    element: createProtectedRoute(HistoryListContainer)
  },
  {
    path: ROUTES.CALL_HISTORY_LIST.FULL_PATH,
    element: createProtectedRoute(CallHistoryCompaniesContainer)
  },
  {
    path: ROUTES.CALL_HISTORY_USERS.FULL_PATH,
    element: createProtectedRoute(CallHistoryCompanyUsersContainer, true)
  },
  {
    path: ROUTES.CALL_HISTORY_ACCESS_LOG.FULL_PATH,
    element: createProtectedRoute(CallHistoryAccessLogContainer, true)
  }
];
